<script setup lang="ts">
import type { Agent } from '@@/shared/agent/types';
import type { IadAppRoute } from '~/application';

const searchTerm = ref('');

const debouncedSearchTerm = refDebounced(searchTerm, 300);
const { data, isPending } = useSearchAgents(debouncedSearchTerm);

const searchResults = computed(() => data.value?.items ?? []);
const hasPerformedRequest = computed(() => debouncedSearchTerm.value.trim().length > 1 && !isPending.value);

function highlightSearchTerm(text: string): string {
  if (!searchTerm.value) return text;
  const searchWords = searchTerm.value.split(' ');
  const textWords = text.split(' ').map((word) => {
    const searchWord = searchWords.find(searchWord => word.toLowerCase().includes(searchWord.toLowerCase()));
    if (searchWord) return word.replace(new RegExp(searchWord, 'gi'), match => `<b>${match}</b>`);
    return word;
  });
  return textWords.join(' ');
}

// CSS var --reka-select-trigger-width isn't computed as we want
const anchorRef = ref<HTMLElement | null>(null);
const { width } = useElementBounding(anchorRef);

const selectedAgent = ref<Agent>();
const localePath = useLocalePath();
const { locale } = useI18n();
watch(selectedAgent, async (newValue) => {
  if (!newValue) return;
  const agentUrl: IadAppRoute<`real-estate-agent-userName`> = {
    name: `real-estate-agent-userName`,
    params: { userName: newValue.userName },
  };
  trackEvent({
    event: 'search',
    event_name: 'search_interaction',
    search_location: 'search_page',
    search_type: 'collaborator',
    search_term: newValue.fullName,
  });
  const path = localePath(agentUrl, locale.value);
  await navigateTo({ path });
});
</script>

<template>
  <ComboboxRoot
    v-model="selectedAgent"
    class="relative"
  >
    <ComboboxAnchor ref="anchorRef">
      <div class="relative border rounded-md border-neutral focus-within:border-secondary bg-white text-neutral-10">
        <ComboboxInput
          v-model="searchTerm"
          :placeholder="$t('agent.search_form.by_name_placeholder')"
          class="px-s py-xs w-full bg-transparent"
        />
        <Icon
          name="iad-icon:search"
          class="absolute top-1/2 right-s -translate-y-1/2 pointer-events-none size-6"
        />
      </div>
    </ComboboxAnchor>
    <ComboboxContent
      class="max-h-60 overflow-auto overscroll-contain
          rounded-md bg-white text-neutral-10 ring-1 ring-neutral-60 shadow-popover
          will-change-[transform,opacity] data-[state=open]:animate-slideDownAndFade empty:hidden"
      position="popper"
      position-strategy="absolute"
      :side-offset="10"
      side="bottom"
      :style="{ width: `${width}px`, zIndex: '50' }"
    >
      <ComboboxVirtualizer
        v-slot="{ option }"
        :options="searchResults"
        :estimate-size="64"
        :text-content="(opt) => opt.fullName"
        class="divide-y divide-neutral-80"
      >
        <ComboboxItem
          class="hover:bg-secondary-70 data-[highlighted]:bg-secondary-70 p-m flex justify-between items-center w-full"
          :value="option"
        >
          <div class="flex items-center gap-m">
            <AgentAvatarBubble :agent="option" />
            <p v-html="highlightSearchTerm(option.fullName)" />
          </div>
          <span
            class="text-neutral text-xs"
            v-text="option.location.place"
          />
        </ComboboxItem>
      </ComboboxVirtualizer>
      <ComboboxEmpty
        v-if="hasPerformedRequest"
        class="p-m flex items-center"
      >
        <span v-text="$t('agent.search_result.no_agent_found')" />
      </ComboboxEmpty>
    </ComboboxContent>
  </ComboboxRoot>
</template>
